import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Generation-progress controller for the Illustrated Carousel wizard's Generate
// step. Subscribes to the shared `shorts_wizard_<config_id>` ActionCable channel
// (the same channel IllustratedCarousel::SlideBackgroundService broadcasts to) and
// updates the progress bar + per-slide status pills as each slide's illustration
// is generated. "Generate all" and per-slide "Regenerate" are fetch POSTs; the
// service streams progress back. Mirrors vocabulary_set_generation_controller.
export default class extends Controller {
  static targets = ["bar", "count", "status", "startButton", "slideRow", "slideStatus"]
  static values = {
    configId: Number,
    startUrl: String,
    nextUrl: String,
  }

  connect() {
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "ShortsWizardChannel", config_id: this.configIdValue },
      { received: (data) => this.handleProgress(data) }
    )
  }

  disconnect() {
    this.subscription?.unsubscribe()
    this.consumer?.disconnect()
  }

  startAll(event) {
    event?.preventDefault()
    this.post(this.startUrlValue, this.hasStartButtonTarget ? this.startButtonTarget : null)
  }

  // Per-slide regenerate — the button carries its own URL in data-url.
  regenerate(event) {
    event?.preventDefault()
    const url = event.currentTarget?.dataset?.url
    if (url) this.post(url, event.currentTarget)
  }

  async post(url, button) {
    this.setStatus("Illustrating…")
    if (button) button.disabled = true
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
      })
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        this.setStatus(`Error: ${body.error || response.statusText}`)
        if (button) button.disabled = false
        return
      }
      // Reload so freshly-generated illustration thumbnails render.
      setTimeout(() => window.location.reload(), 900)
    } catch (err) {
      this.setStatus(`Error: ${err.message}`)
      if (button) button.disabled = false
    }
  }

  handleProgress(data) {
    if (data.percentage != null) this.setProgress(data.percentage)
    if (data.message) this.setStatus(data.message)

    const match = data.step?.match(/generate_slide_(\d+)/)
    if (match) {
      const index = parseInt(match[1], 10)
      const row = this.slideRowTargets.find((el) => parseInt(el.dataset.slideIndex, 10) === index)
      const pill = row?.querySelector("[data-illustrated-carousel-generation-target='slideStatus']")
      if (pill) {
        pill.classList.remove("ic-slide__status--pending", "ic-slide__status--failed")
        pill.classList.add("ic-slide__status--ready")
        pill.textContent = "Illustrated"
      }
    }
  }

  setProgress(percentage) {
    if (this.hasBarTarget) this.barTarget.style.width = `${percentage}%`
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
